/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import {
  FoodmaxapipbGetOrderUnchangeableReasonsResponse,
  FoodmaxapipbGetMerchantReportSummaryResponse,
} from "generated/foodmaxapi.type"
import { ReviewOrderChangesResponse } from "order-manager/types/api/reviewOrderChanges.type"
import { OrderEtaResponse } from "order-manager/types/api/orderFetchEta.type"
import { FetchOrderByIDResponse } from "order-manager/types/api/orderFetchOrderByID.type"
import { FetchHistoryListResponse } from "order-manager/types/api/orderFetchHistoryList.type"
import { FetchOrderListResponse } from "order-manager/types/api/orderFetchOrderList.type"
import { markStatus } from "order-manager/constants/mark"
import { DEFAULT_PAGE_SIZE } from "order-manager/constants/api"
import { createRequestHeaders } from "./apiClient"
import { endpoints, dpApiEndpoints } from "./constants"
import {
  FetchOrderCancelReasonsRequest,
  CancelOrderRequest,
  FetchOrderSummaryRequest,
  HttpMethod,
  FetchOrderEtaRequest,
  FetchOrderExportedFileRequest,
  FetchOrderExportedFileResponse,
  FetchOrderExportedMpaFileRequest,
  FetchOrderExportedMpaFileResponse,
  FetchOrderByIDRequest,
  FetchOrderListRequest,
  MarkOrderRequest,
  MarkOrderDeliverBody,
  ReviewOrderChangesRtkRequest,
  SubmitOrderChangesRequest,
  UpdateStoreStatusRequest,
  FetchHistoryListRequest,
} from "./types"
import { foodMerchantBaseApi, dpApiBaseApi } from "./baseApis"

export const orderManagerApi = foodMerchantBaseApi.injectEndpoints({
  endpoints: builder => ({
    fetchOrderCancelReasons: builder.query<
      FoodmaxapipbGetOrderUnchangeableReasonsResponse,
      FetchOrderCancelReasonsRequest
    >({
      query: ({ merchantID, orderID, locale }) => ({
        url: endpoints.fetchOrderCancelReasons,
        headers: createRequestHeaders({ merchantID }),
        params: { orderID, locale },
      }),
    }),
    fetchOrderSummary: builder.query<
      FoodmaxapipbGetMerchantReportSummaryResponse,
      FetchOrderSummaryRequest
    >({
      query: ({ merchantID, startDate }) => ({
        url: endpoints.fetchOrderSummary,
        headers: createRequestHeaders({ merchantID }),
        params: { startDate },
      }),
    }),
    reviewOrderChanges: builder.mutation<
      ReviewOrderChangesResponse,
      ReviewOrderChangesRtkRequest
    >({
      query: ({ orderID, merchantID, data }) => ({
        url: `${endpoints.reviewOrderChanges}/${orderID}`,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID }),
        data,
      }),
    }),
    fetchOrderEta: builder.query<OrderEtaResponse, FetchOrderEtaRequest>({
      query: ({ merchantID, orderIDs }) => ({
        url: endpoints.fetchOrderEta,
        headers: createRequestHeaders({ merchantID }),
        params: { OrderIDs: orderIDs },
      }),
    }),
    submitOrderChanges: builder.mutation<void, SubmitOrderChangesRequest>({
      query: ({ merchantID, orderID, orderSignatureID }) => ({
        url: `${endpoints.submitOrderChanges}/${orderID}`,
        method: HttpMethod.PUT,
        data: { orderSignatureID, orderID },
        headers: createRequestHeaders({ merchantID }),
        extraOptions: { showGlobalLoading: true },
      }),
    }),
    cancelOrder: builder.mutation<void, CancelOrderRequest>({
      query: ({ merchantID, orderID, cancelReason, cancelCode }) => ({
        url: `${endpoints.cancelOrder}/${orderID}`,
        method: HttpMethod.PUT,
        data: { orderID, cancelReason, cancelCode },
        headers: createRequestHeaders({ merchantID }),
      }),
    }),
    fetchOrderByID: builder.query<
      FetchOrderByIDResponse,
      FetchOrderByIDRequest
    >({
      query: ({ merchantID, orderID }) => ({
        url: `${endpoints.fetchOrderByID}/${orderID}`,
        method: HttpMethod.GET,
        headers: createRequestHeaders({ merchantID }),
      }),
    }),
    updateStoreStatus: builder.mutation<void, UpdateStoreStatusRequest>({
      query: ({ merchantID, params }) => ({
        url: endpoints.updateStoreStatus,
        method: HttpMethod.PUT,
        data: params,
        headers: createRequestHeaders({ merchantID }),
      }),
    }),
    markOrder: builder.mutation<void, MarkOrderRequest>({
      query: ({ merchantID, data, status }) => {
        let url: string
        if (status === markStatus.Delivering) {
          url = endpoints.deliverByMex
        } else if (status === markStatus.Delivered) {
          url = `${endpoints.terminateOrder}/${
            (data as MarkOrderDeliverBody).orderID
          }/terminate`
        } else {
          url = endpoints.markOrder
        }

        return {
          url,
          method: HttpMethod.POST,
          headers: createRequestHeaders({ merchantID }),
          data,
        }
      },
    }),
  }),
})

export const {
  useCancelOrderMutation,
  useFetchOrderByIDQuery,
  useFetchOrderCancelReasonsQuery,
  useFetchOrderEtaQuery,
  useFetchOrderSummaryQuery,
  useLazyFetchOrderByIDQuery,
  useLazyFetchOrderCancelReasonsQuery,
  useLazyFetchOrderEtaQuery,
  useLazyFetchOrderSummaryQuery,
  useMarkOrderMutation,
  useReviewOrderChangesMutation,
  useSubmitOrderChangesMutation,
  useUpdateStoreStatusMutation,
} = orderManagerApi

/**
 * DelvPlatform API endpoints injected into dpApiBaseApi.
 */
export const orderManagerDpApi = dpApiBaseApi.injectEndpoints({
  endpoints: builder => ({
    fetchHistoryList: builder.query<
      FetchHistoryListResponse,
      FetchHistoryListRequest
    >({
      query: ({
        merchantID,
        states = [],
        startTime,
        endTime,
        pageIndex = 0,
        pageSize = DEFAULT_PAGE_SIZE,
      }) => ({
        url: dpApiEndpoints.fetchHistoryList,
        headers: createRequestHeaders({ merchantID }),
        params: {
          states: states.join(","),
          startTime,
          endTime,
          pageIndex,
          pageSize,
        },
        extraOptions: { showError: true, showGlobalLoading: false },
      }),
    }),
    fetchOrderExportedFile: builder.mutation<
      FetchOrderExportedFileResponse,
      FetchOrderExportedFileRequest
    >({
      query: ({ merchantID, orderIDs }) => ({
        url: dpApiEndpoints.fetchOrderExportedFile,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID }),
        data: { orderIDs },
      }),
    }),
    fetchOrderExportedMpaFile: builder.mutation<
      FetchOrderExportedMpaFileResponse,
      FetchOrderExportedMpaFileRequest
    >({
      query: ({ merchantID, startTime, endTime }) => ({
        url: dpApiEndpoints.fetchOrderExportedMpaFile,
        method: HttpMethod.POST,
        headers: createRequestHeaders({ merchantID }),
        data: { startTime, endTime },
      }),
    }),
    fetchOrderList: builder.query<
      FetchOrderListResponse,
      FetchOrderListRequest
    >({
      query: ({
        merchantID,
        pageType,
        autoAcceptGroup,
        searchToken,
        size = DEFAULT_PAGE_SIZE,
      }) => ({
        url: dpApiEndpoints.fetchOrderList,
        method: HttpMethod.GET,
        headers: createRequestHeaders({ merchantID }),
        params: {
          AutoAcceptGroup: autoAcceptGroup,
          merchantID,
          PageType: pageType,
          searchToken,
          size,
        },
        extraOptions: { showGlobalLoading: false },
      }),
    }),
  }),
})

export const {
  useLazyFetchHistoryListQuery,
  useFetchOrderExportedFileMutation,
  useFetchOrderExportedMpaFileMutation,
  useFetchOrderListQuery,
  useLazyFetchOrderListQuery,
} = orderManagerDpApi
